import PermissionButton from '../../../permissions/PermissionButton';
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  InputNumber,
  message,
  Space,
  Divider,
  Card
} from 'antd';
import {
  PrinterOutlined,
  EditOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { printCartonLabels, printCartonLabelsThermal } from './PreparationPrintUtility';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PreparationPrintModal = ({ 
  visible, 
  onCancel, 
  bonlivData = {} 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState({});

  // Initialize form values when modal opens or bonlivData changes
  useEffect(() => {
    if (visible && bonlivData) {
      const initialValues = {
        clientName: bonlivData.CLIENT || '',
        address: bonlivData.client?.ADRESSE_SIEGE || bonlivData.client?.ADRESSE1 || '',
        city: bonlivData.client?.VILLE || '',
        phoneNumber: bonlivData.client?.TEL || bonlivData.client?.TELEPHONE || '',
        transportMode: bonlivData.MODELIV || '',
        commercial: bonlivData.commercial?.nom || '',
        nbrCartons: bonlivData.nbr_colis || 1,
        refBL: bonlivData.REF_BL || '',
        REF_DEV: bonlivData.REF_DEV || ''
      };
      
      form.setFieldsValue(initialValues);
      setPreviewData(initialValues);
    }
  }, [visible, bonlivData, form]);

  // Update preview data when form values change
  const handleFormChange = (changedValues, allValues) => {
    setPreviewData(allValues);
  };

  const handlePrint = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Validation
      if (!values.nbrCartons || values.nbrCartons < 1) {
        message.error('Le nombre de cartons doit être au moins 1');
        return;
      }

      if (values.nbrCartons > 50) {
        message.error('Le nombre de cartons ne peut pas dépasser 50');
        return;
      }

      // Prepare data for printing
      const printData = {
        clientName: values.clientName || 'N/A',
        address: values.address || 'N/A',
        city: values.city || 'N/A',
        phoneNumber: values.phoneNumber || 'N/A',
        transportMode: values.transportMode || 'N/A',
        commercial: values.commercial || 'N/A',
        nbrCartons: values.nbrCartons,
        REF_DEV: values.REF_DEV || 'N/A',
        refBL: values.refBL || 'N/A'
      };

      // Call print utility with header
      await printCartonLabels(printData, true);
      
      message.success(`Impression lancée pour ${values.nbrCartons} étiquette(s) carton`);
      onCancel(); // Close modal after printing
      
    } catch (error) {
      console.error('Print error:', error);
      message.error('Erreur lors de l\'impression');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintWithoutHeader = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Validation
      if (!values.nbrCartons || values.nbrCartons < 1) {
        message.error('Le nombre de cartons doit être au moins 1');
        return;
      }

      if (values.nbrCartons > 50) {
        message.error('Le nombre de cartons ne peut pas dépasser 50');
        return;
      }

      // Prepare data for printing
      const printData = {
        clientName: values.clientName || 'N/A',
        address: values.address || 'N/A',
        city: values.city || 'N/A',
        phoneNumber: values.phoneNumber || 'N/A',
        transportMode: values.transportMode || 'N/A',
        commercial: values.commercial || 'N/A',
        nbrCartons: values.nbrCartons,
        REF_DEV: values.REF_DEV || 'N/A',
        refBL: values.refBL || 'N/A'
      };

      // Call print utility without header
      await printCartonLabels(printData, false);
      
      message.success(`Impression sans entête lancée pour ${values.nbrCartons} étiquette(s) carton`);
      onCancel(); // Close modal after printing
      
    } catch (error) {
      console.error('Print error:', error);
      message.error('Erreur lors de l\'impression');
    } finally {
      setLoading(false);
    }
  };

  // Thermal test print: one 101.6mm x 101.6mm label per page (Godex GE300)
  const handlePrintThermal = async (showHeader) => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      // Validation
      if (!values.nbrCartons || values.nbrCartons < 1) {
        message.error('Le nombre de cartons doit être au moins 1');
        return;
      }

      if (values.nbrCartons > 50) {
        message.error('Le nombre de cartons ne peut pas dépasser 50');
        return;
      }

      // Prepare data for printing
      const printData = {
        clientName: values.clientName || 'N/A',
        address: values.address || 'N/A',
        city: values.city || 'N/A',
        phoneNumber: values.phoneNumber || 'N/A',
        transportMode: values.transportMode || 'N/A',
        commercial: values.commercial || 'N/A',
        nbrCartons: values.nbrCartons,
        REF_DEV: values.REF_DEV || 'N/A',
        refBL: values.refBL || 'N/A'
      };

      await printCartonLabelsThermal(printData, showHeader);

      message.success(`Impression test (thermique) lancée pour ${values.nbrCartons} étiquette(s) carton`);
      onCancel(); // Close modal after printing

    } catch (error) {
      console.error('Print error:', error);
      message.error('Erreur lors de l\'impression');
    } finally {
      setLoading(false);
    }
  };

  const calculatePages = (nbrCartons) => {
    if (!nbrCartons || nbrCartons < 1) return 0;
    return Math.ceil(nbrCartons / 4); // 4 labels per page
  };

  const getPrintPreview = () => {
    const pages = calculatePages(previewData.nbrCartons);
    if (pages === 0) return 'Aucune étiquette';
    
    if (pages === 1) {
      return `${previewData.nbrCartons} étiquette(s) sur 1 page`;
    }
    
    const lastPageLabels = previewData.nbrCartons % 4;
    const fullPages = Math.floor(previewData.nbrCartons / 4);
    
    let preview = `${previewData.nbrCartons} étiquettes sur ${pages} pages : `;
    if (fullPages > 0) {
      preview += `${fullPages} page(s) avec 4 étiquettes`;
    }
    if (lastPageLabels > 0) {
      if (fullPages > 0) preview += ', ';
      preview += `1 page avec ${lastPageLabels} étiquette(s)`;
    }
    
    return preview;
  };

  return (
    <Modal
      title={
        <Space>
          <PrinterOutlined />
          <span>Impression Étiquettes Cartons</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annuler
        </Button>,
        <PermissionButton permissionModule="preparation" permissionAction="print"
          key="print-test"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={() => handlePrintThermal(true)}
          loading={loading}
          disabled={!previewData.nbrCartons || previewData.nbrCartons < 1}
        >
          Imprimer
        </PermissionButton>,
        <PermissionButton permissionModule="preparation" permissionAction="print"
          key="print-test-no-header"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={() => handlePrintThermal(false)}
          loading={loading}
          disabled={!previewData.nbrCartons || previewData.nbrCartons < 1}
        >
          Imprimer sans entête
        </PermissionButton>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        requiredMark={false}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Card 
              size="small" 
              title={
                <Space>
                  <InfoCircleOutlined />
                  <span>Aperçu d'impression</span>
                </Space>
              }
              style={{ marginBottom: 16, backgroundColor: '#f9f9f9' }}
            >
              <Text strong>Référence DEV: </Text>
              <Text>{previewData.REF_DEV || 'N/A'}</Text>
              <br />
              <Text strong>Nombre d'étiquettes: </Text>
              <Text>{previewData.nbrCartons || 0}</Text>
              <br />
              <Text strong>Disposition: </Text>
              <Text type="secondary">{getPrintPreview()}</Text>
            </Card>
          </Col>
        </Row>

        <Divider orientation="left">
          <Space>
            <EditOutlined />
            <span>Informations à imprimer</span>
          </Space>
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Nom du Client"
              name="clientName"
              rules={[{ required: true, message: 'Le nom du client est requis' }]}
            >
              <Input 
                placeholder="Nom du client"
                size="small"
              />
            </Form.Item>
            <Form.Item name="REF_DEV" hidden>
  <Input />
</Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Numéro de Téléphone"
              name="phoneNumber"
            >
              <Input 
                placeholder="Numéro de téléphone"
                size="small"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Adresse"
              name="address"
            >
              <TextArea 
                placeholder="Adresse complète"
                rows={2}
                size="small"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Ville"
              name="city"
            >
              <Input 
                placeholder="Ville"
                size="small"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Mode de Transport/Livraison"
              name="transportMode"
            >
              <Input 
                placeholder="Mode de transport"
                size="small"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Commercial"
              name="commercial"
            >
              <Input 
                placeholder="Nom du commercial"
                size="small"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Référence BL"
              name="refBL"
            >
              <Input 
                placeholder="Référence du bon de livraison"
                disabled
                size="small"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Nombre de Cartons"
              name="nbrCartons"
              rules={[
                { required: true, message: 'Le nombre de cartons est requis' },
                { type: 'number', min: 1, max: 50, message: 'Entre 1 et 50 cartons' }
              ]}
            >
              <InputNumber
                min={1}
                max={50}
                placeholder="Nombre de cartons"
                style={{ width: '100%' }}
                size="small"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Card 
        size="small" 
        title="Instructions d'impression" 
        style={{ marginTop: 16, backgroundColor: '#f0f9ff' }}
      >
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: '12px' }}>
          <li>Les étiquettes seront imprimées sur papier A4 en orientation paysage</li>
          <li>Maximum 4 étiquettes par page (2x2)</li>
          <li>Chaque étiquette aura un compteur (ex: 1/3, 2/3, 3/3)</li>
          <li>Les étiquettes excédentaires seront placées sur les pages suivantes</li>
        </ul>
      </Card>
    </Modal>
  );
};

export default PreparationPrintModal; 